import { ChangeEvent } from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IAlertsService } from '../../../infrastructure/alerts';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { showNotification } from '../../global/modal/alerts-host/alert-templates';
import { ICreateThemeDTO, IThemeDTO, IThemesDAO, IUpdateThemeDTO } from '../../../api/themes.dao';
import { ThemeFormDataVM } from './theme-form-data.viewmodel';

class ThemeFormVM {

    @mobx.observable
    public saving: boolean;
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    public themeFormDataVM: ThemeFormDataVM;

    public themeName: string;

    @mobx.observable
    public themeArchive: File;

    constructor(
        private fetchThemesList: () => void,
        public theme: IThemeDTO,
        @IoC.Inject private readonly themesDAO: IThemesDAO = IoC.Container.get(IThemesDAO),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
        this.themeFormDataVM = new ThemeFormDataVM();
        this.themeFormDataVM.initialize(theme);
    }

    public isSaving() {
        return this.saving;
    }

    @mobx.action
    public setThemeArchive = (e: ChangeEvent<HTMLInputElement>) => {
        this.themeArchive = e.currentTarget.files[0];
    };

    public getFormDataVM(): ThemeFormDataVM {
        return this.themeFormDataVM;
    }

    public getErrorMessage(intl: IntlShape): string | null {
        const {showValidationErrors, getThemeNameError, getThemeArchiveError, serverErrorMessage} = this.themeFormDataVM;
        if (showValidationErrors()) {
            if (getThemeNameError(intl)) {
                return getThemeNameError(intl);
            } else if (getThemeArchiveError(intl)) {
                return getThemeArchiveError(intl);
            }
        }
        return serverErrorMessage;
    }

    public async handleApiType(requestParams: ICreateThemeDTO | IUpdateThemeDTO, requestHandler: (params) => void) {
        await requestHandler({...requestParams});
    }

    @mobx.action
    public handleSubmit = async (intl: IntlShape) => {
        this.getFormDataVM().serverErrorMessage = '';
        const isFormInvalid = !this.getFormDataVM().isFormValid(intl);
        if (isFormInvalid) {
            this.getFormDataVM().allowToShowValidationErrors();
            return false;
        }
        try {
            this.saving = true;
            const themeArchive = this.themeFormDataVM.themeArchive;

            await this.handleApiType(
                this.theme ? this.themeFormDataVM.toApiUpdateThemeDTO(themeArchive) : this.themeFormDataVM.toApiCreateThemeDTO(themeArchive),
                params => this.theme ? this.themesDAO.updateTheme(params) : this.themesDAO.createTheme(params)
            );
            showNotification(
                this.theme ?
                    intl.formatMessage({
                        id: 'oe.theme.themeUpdated',
                        description: 'Notification about updated theme',
                        defaultMessage: 'Theme updated',
                    }) :
                    intl.formatMessage({
                        id: 'oe.theme.themeAdded',
                        description: 'Notification about added theme',
                        defaultMessage: 'New theme added',
                    })
            );

            this.fetchThemesList();
            return true;
        } catch (error) {
            this.onCreateThemeError(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    @mobx.action
    private onCreateThemeError(error): void {
        const apiError = this.errorParsingService.extractAPIError(error);
        if (apiError) {
            this.getFormDataVM().serverErrorMessage = apiError.errorMessage;
        } else {
            this.alertsService.alert({message: this.errorParsingService.extractMessage(error)});
        }
    }
}

export { ThemeFormVM };
